import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { getCompanies } from "../../features/companies/companiesSlice";
import DashboardCard from "./components/DashboardCard";
import CompanyFilter from "../../components/common/CompanyFilter";
import {
  FaRobot,
  FaWhatsapp,
  FaUserPlus,
  FaBuilding,
  FaEnvelope,
  FaSms,
  FaHashtag,
  FaComments,
  FaChartLine,
} from "react-icons/fa";
import { Icon } from "../../components/common/Icon";
import { useTranslation } from "react-i18next";

const MarketingDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { selectedAgencyId, selectedClientId } = useSelector(
    (state: RootState) => state.settings
  );
  const { t } = useTranslation();

  const [selectedAgency, setSelectedAgency] = useState<number | undefined>(
    selectedAgencyId
  );
  const [selectedClient, setSelectedClient] = useState<number | undefined>(
    selectedClientId
  );

  // Dummy data
  const dashboardData = {
    aiChatbots: 12,
    whatsappChatbots: 8,
    leadGeneration: 145,
    companyResearch: 24,
    emailMarketing: 32,
    whatsappMarketing: 17,
    socialMedia: 56,
    communicationsHub: 93,
  };

  useEffect(() => {
    dispatch(getCompanies({}));
  }, [dispatch]);

  const handleAgencyChange = (agencyId: number | undefined) => {
    setSelectedAgency(agencyId);
    if (agencyId) {
      dispatch({ type: "settings/setSelectedAgency", payload: agencyId });
    } else {
      dispatch({ type: "settings/setSelectedAgency", payload: undefined });
    }
    setSelectedClient(undefined);
    dispatch({ type: "settings/setSelectedClient", payload: undefined });
  };

  const handleClientChange = (clientId: number | undefined) => {
    setSelectedClient(clientId);
    dispatch({ type: "settings/setSelectedClient", payload: clientId });
  };

  // Client seçili mi kontrol et
  const isClientSelected = !!selectedClient;

  return (
    <Container fluid className="marketing-dashboard">
      {/* Modern Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-info">
            <div className="header-icon">
              <Icon icon={FaChartLine} />
            </div>
            <div className="header-text">
              <h1 className="page-title">{t("marketing_dashboard")}</h1>
              <p className="page-subtitle">
                {t("welcome_back")}, {user?.full_name || t("user")}!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <Card className="filter-card">
        <Card.Header className="card-header-custom">
          <div className="section-header">
            <div className="section-icon filter-icon">
              <i className="bi bi-funnel"></i>
            </div>
            <h5 className="section-title">{t("filters")}</h5>
          </div>
        </Card.Header>
        <Card.Body className="card-body-custom">
          <CompanyFilter
            selectedAgency={selectedAgency}
            selectedClient={selectedClient}
            onAgencyChange={handleAgencyChange}
            onClientChange={handleClientChange}
          />
          {!isClientSelected && (
            <div className="alert alert-warning mt-3">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {t("Please select a client company to access marketing tools")}
            </div>
          )}
        </Card.Body>
      </Card>

{/* Marketing Tools Card */}
<Card className="content-card">
  <Card.Header className="card-header-custom">
    <div className="section-header">
      <div className="section-icon tools-icon">
        <i className="bi bi-tools"></i>
      </div>
      <h5 className="section-title">{t("marketing_tools")}</h5>
    </div>
  </Card.Header>
  <Card.Body className="card-body-custom">
    <Row xs={1} sm={2} md={3} lg={4} className="g-4">
      <Col>
        <DashboardCard
          title={t("ai_chatbots")}
          icon={<Icon icon={FaRobot} size={24} />}
          count={dashboardData.aiChatbots}
          color="#4e73df"
          path="/ai-chatbots"
          isEnabled={isClientSelected}
        />
      </Col>

      <Col>
        <DashboardCard
          title={t("whatsapp_chatbots")}
          icon={<Icon icon={FaWhatsapp} size={24} />}
          count={dashboardData.whatsappChatbots}
          color="#1cc88a"
          path="/marketing-dashboard/whatsapp-chatbots"
          isEnabled={false}
          comingSoon={true}
        />
      </Col>

      <Col>
        <DashboardCard
          title={t("lead_generation")}
          icon={<Icon icon={FaUserPlus} size={24} />}
          count={dashboardData.leadGeneration}
          color="#f6c23e"
          path="/marketing-dashboard/lead-generation"
          isEnabled={false}
          comingSoon={true}
        />
      </Col>

      <Col>
        <DashboardCard
          title={t("company_research")}
          icon={<Icon icon={FaBuilding} size={24} />}
          count={dashboardData.companyResearch}
          color="#e74a3b"
          path="/marketing-dashboard/company-research"
          isEnabled={false}
          comingSoon={true}
        />
      </Col>

      <Col>
        <DashboardCard
          title={t("email_marketing")}
          icon={<Icon icon={FaEnvelope} size={24} />}
          count={dashboardData.emailMarketing}
          color="#36b9cc"
          path="/marketing-dashboard/email-marketing"
          isEnabled={false}
          comingSoon={true}
        />
      </Col>

      <Col>
        <DashboardCard
          title={t("whatsapp_marketing")}
          icon={<Icon icon={FaSms} size={24} />}
          count={dashboardData.whatsappMarketing}
          color="#6f42c1"
          path="/marketing-dashboard/whatsapp-marketing"
          isEnabled={false}
          comingSoon={true}
        />
      </Col>

      <Col>
        <DashboardCard
          title={t("social_media")}
          icon={<Icon icon={FaHashtag} size={24} />}
          count={dashboardData.socialMedia}
          color="#fd7e14"
          path="/marketing-dashboard/social-media-management"
          isEnabled={true}
          comingSoon={false}
        />
      </Col>

      <Col>
        <DashboardCard
          title={t("communications_hub")}
          icon={<Icon icon={FaComments} size={24} />}
          count={dashboardData.communicationsHub}
          color="#20c997"
          path="/marketing-dashboard/communications-hub"
          isEnabled={false}
          comingSoon={true}
        />
      </Col>
    </Row>
  </Card.Body>
</Card>

      {/* Statistics Card */}
      <Card className="content-card">
        <Card.Header className="card-header-custom">
          <div className="section-header">
            <div className="section-icon stats-icon">
              <i className="bi bi-graph-up"></i>
            </div>
            <h5 className="section-title">{t("marketing_statistics")}</h5>
          </div>
        </Card.Header>
        <Card.Body className="card-body-custom">
          <div className="coming-soon">
            <div className="coming-soon-icon">
              <Icon icon={FaChartLine} size="3rem" />
            </div>
            <h6 className="coming-soon-title">{t("statistics_coming_soon")}</h6>
            <p className="coming-soon-text">
              {t("this_section_will_display_marketing_statistics")}
            </p>
          </div>
        </Card.Body>
      </Card>

      <style>{`
        /* Root Variables for Theme Support */
        .marketing-dashboard {
          --bg-primary: #ffffff;
          --bg-secondary: #f8fafc;
          --bg-tertiary: #f1f5f9;
          --text-primary: #0f172a;
          --text-secondary: #475569;
          --text-muted: #64748b;
          --border-color: #e2e8f0;
          --shadow-light: 0 1px 3px rgba(0, 0, 0, 0.1);
          --shadow-medium: 0 4px 6px rgba(0, 0, 0, 0.07);
          --accent-primary: #3b82f6;
          --accent-secondary: #8b5cf6;
          --success-bg: #dcfce7;
          --success-text: #166534;
          --warning-bg: #fef3c7;
          --warning-text: #92400e;
          padding: 24px;
          background: var(--bg-secondary);
          min-height: 100vh;
        }

        /* Dark Theme */
        [data-bs-theme="dark"] .marketing-dashboard {
          --bg-primary: #1e293b;
          --bg-secondary: #0f172a;
          --bg-tertiary: #334155;
          --text-primary: #f8fafc;
          --text-secondary: #cbd5e1;
          --text-muted: #94a3b8;
          --border-color: #475569;
          --shadow-light: 0 1px 3px rgba(0, 0, 0, 0.3);
          --shadow-medium: 0 4px 6px rgba(0, 0, 0, 0.2);
          --success-bg: #064e3b;
          --success-text: #6ee7b7;
          --warning-bg: #451a03;
          --warning-text: #fbbf24;
        }

        /* Header Styles */
        .page-header {
          margin-bottom: 24px;
          padding: 20px 24px;
          background: var(--bg-primary);
          border-radius: 16px;
          box-shadow: var(--shadow-light);
          border: 1px solid var(--border-color);
          transition: all 0.3s ease;
        }

        .header-content {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          gap: 20px;
        }

        .header-info {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(
            135deg,
            var(--accent-primary) 0%,
            var(--accent-secondary) 100%
          );
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 20px;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
          transition: all 0.3s ease;
        }

        .header-icon:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.35);
        }

        .header-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .page-title {
          font-size: 24px;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
          line-height: 1.2;
        }

        .page-subtitle {
          font-size: 14px;
          color: var(--text-muted);
          margin: 0;
          font-weight: 500;
        }

        /* Card Styles */
        .filter-card,
        .content-card {
          background: var(--bg-primary) !important;
          border: 1px solid var(--border-color) !important;
          box-shadow: var(--shadow-light) !important;
          margin-bottom: 24px;
          border-radius: 16px !important;
          transition: all 0.3s ease;
        }

        .filter-card:hover,
        .content-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-medium) !important;
        }

        .card-header-custom {
          background: var(--bg-secondary) !important;
          border-bottom: 1px solid var(--border-color) !important;
          border-radius: 16px 16px 0 0 !important;
          padding: 20px 24px !important;
        }

        .card-body-custom {
          padding: 24px !important;
        }

        /* Section Headers */
        .section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 0;
        }

        .section-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        .section-icon.filter-icon {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
        }

        .section-icon.tools-icon {
          background: linear-gradient(135deg, #10b981, #059669);
        }

        .section-icon.stats-icon {
          background: linear-gradient(135deg, #f59e0b, #d97706);
        }

        .section-title {
          color: var(--text-primary) !important;
          font-weight: 600 !important;
          font-size: 16px !important;
          margin: 0 !important;
        }

        /* Coming Soon Section */
        .coming-soon {
          text-align: center;
          padding: 40px 20px;
        }

        .coming-soon-icon {
          width: 80px;
          height: 80px;
          background: var(--bg-tertiary);
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 24px;
          color: var(--text-muted);
          box-shadow: var(--shadow-light);
        }

        .coming-soon-title {
          color: var(--text-secondary);
          margin-bottom: 12px;
          font-weight: 600;
        }

        .coming-soon-text {
          color: var(--text-muted);
          margin-bottom: 0;
          font-size: 14px;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .marketing-dashboard {
            padding: 16px;
          }

          .page-header {
            padding: 16px 20px;
            margin-bottom: 20px;
          }

          .header-content {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          .header-info {
            gap: 12px;
          }

          .header-icon {
            width: 40px;
            height: 40px;
            font-size: 18px;
          }

          .page-title {
            font-size: 20px;
          }

          .page-subtitle {
            font-size: 13px;
          }

          .card-header-custom {
            padding: 16px 20px !important;
          }

          .card-body-custom {
            padding: 20px !important;
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .section-icon {
            width: 36px;
            height: 36px;
            font-size: 14px;
          }

          .section-title {
            font-size: 15px !important;
          }

          .coming-soon {
            padding: 30px 20px;
          }

          .coming-soon-icon {
            width: 60px;
            height: 60px;
          }
        }

        @media (max-width: 480px) {
          .marketing-dashboard {
            padding: 12px;
          }

          .page-header {
            padding: 12px 16px;
            border-radius: 12px;
          }

          .filter-card,
          .content-card {
            border-radius: 12px !important;
          }

          .card-header-custom {
            border-radius: 12px 12px 0 0 !important;
            padding: 12px 16px !important;
          }

          .card-body-custom {
            padding: 16px !important;
          }

          .header-icon {
            width: 36px;
            height: 36px;
            font-size: 16px;
            border-radius: 10px;
          }

          .section-icon {
            width: 32px;
            height: 32px;
            font-size: 13px;
            border-radius: 8px;
          }

          .coming-soon {
            padding: 24px 16px;
          }

          .coming-soon-icon {
            width: 50px;
            height: 50px;
          }

          .coming-soon-title {
            font-size: 16px;
          }

          .coming-soon-text {
            font-size: 13px;
          }
        }

        /* Animation */
        .page-header {
          animation: slideInUp 0.6s ease-out;
        }

        .filter-card,
        .content-card {
          animation: fadeIn 0.8s ease-out;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        /* Smooth Transitions */
        * {
          transition:
            background-color 0.3s ease,
            color 0.3s ease,
            border-color 0.3s ease;
        }
      `}</style>
    </Container>
  );
};

export default MarketingDashboard;
